// Enquiries (Session ARE, Director decision D82): the reply-drafting review queue built on top of
// the existing email-triage sweep. This is the drafting-capable subset -- the same "action owed",
// undismissed population InboxTriagePage.tsx's own "Action owed" section lists -- enriched with a
// customer match, an AI-drafted reply and a one-click approve-and-send once the office has read it.
//
// src/routes/office/InboxTriagePage.tsx (mounted at /office/inbox-triage, "Emails to answer" in the
// nav) stays exactly as it is: the raw sweep view over every triage state, with Dismiss/Restore.
// This page is additive, not a replacement, and does not touch that page, its route or its nav
// entry.
//
// Session ARM (Director decision D88, option (a), 25 September 2026): PHONE ENQUIRIES JOIN THE EMAIL
// ONES HERE, so every unknown enquiry is in one place. An unmatched logged call no longer makes a
// hidden sales lead; the call itself is the record, and it is listed here until the office matches
// it to a customer, raises work from it, or deletes it. It is a CLIENT-SIDE UNION of two existing
// reads (the enquiry list, and GET /api/call-logs?unmatched=true), deliberately: an Enquiry is an
// InboxTriageThread keyed on a Gmail thread, and a call is not, so nothing on the server merges them.
// The global Call log page stays retired (Director, 17 August 2026): this page is where an unknown
// caller surfaces now, not a re-routed copy of that one.
//
// A PHONE ROW NEVER OPENS EnquiryDetailPage: that page reads email threads only and would answer "That
// enquiry could not be found". Its actions sit on the row instead, each reusing what already exists:
// Match (MatchCallModal, which sends the customer and nothing else), Raise work (the SAME
// AcceptCallAsWorkModal and RaiseWorkModal the customer Call log tab uses, carrying the call), and
// Delete (the existing soft delete, two-step).
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import type { CallLogDto, EnquiryDetail, EnquiryListRow, EnquiryStatus, RaisedWorkRef } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { useListSearch, filterRows, type SearchAccessors } from "../../lib/useListSearch";
import { PageHeader } from "../../components/PageHeader";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { StatusPill } from "../../components/StatusPill";
import { Loader } from "../../components/Loader";
import { formatDateTime } from "../../lib/format";
import { statusEntry } from "../../lib/statusRegistry";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Button } from "../../components/Button";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { raisedWorkLabel } from "../../components/CallLogTable";
import {
  ENQUIRY_STATUS_LABEL,
  DRAFT_TIER_LABEL,
  enquiryDraftSource,
  counterpartyLine,
  draftBodySummary,
} from "./enquiriesUi";
import { AcceptCallAsWorkModal, callerLine } from "./AcceptCallAsWorkModal";
import { RaiseWorkModal, raisedWorkMessage } from "./RaiseWorkModal";
import { MatchCallModal } from "./MatchCallModal";
import { EnquirySendConfirmModal } from "./EnquirySendConfirmModal";
import { EnquiryBacklogPanel } from "./EnquiryBacklogPanel";

// Session ATM, Part C: the bulk-approve screen. Rows that qualify for it (see readyRowsOf /
// needsDecisionRowsOf below) are a SUBSET of the same `enquiries` list this page already loads --
// there is no second fetch and no new list route. See BulkApproveView near the bottom of this
// file for the two sections themselves.
// Session ATZ (Item 4): "clear" is the backlog clearance (EnquiryBacklogPanel.tsx).
type PageView = "queue" | "bulk" | "clear";

// status === "awaiting_review" && draftTier === "templated" -- the email engine's own trust gate
// already passed every check for these (docs/specs/ELE-V2-SESSION-ATM-SPEC.md, Part C.2).
export function readyRowsOf(list: EnquiryListRow[]): EnquiryListRow[] {
  return list.filter((e) => e.status === "awaiting_review" && e.draftTier === "templated");
}

// draftTier === "novel" (Part B's judgement-category cut) and not yet sent -- read-only here,
// structurally separate from the bulk list above (never a checkbox, never a shared array).
export function needsDecisionRowsOf(list: EnquiryListRow[]): EnquiryListRow[] {
  return list.filter((e) => e.draftTier === "novel" && (e.status === "drafted" || e.status === "awaiting_review"));
}

// "Needs action" is the practical default: everything short of Sent. A separate explicit "Sent"
// chip keeps the history reachable without it crowding the daily working view -- given the real
// volume here (a handful of enquiries a day), fetching the whole list once and filtering it in the
// browser is simpler to reason about and to test than re-querying the server per chip, and keeps
// every filter change instant.
//
// Session ARM: "Needs action" and "All" cover phone rows too (a phone row needs action until work has
// been raised from it). The other chips are EMAIL drafting states, unchanged, so they show email rows
// only: a phone call has no draft and no send.
type EnquiryFilter = "needs_action" | EnquiryStatus | "all";
const FILTERS: { value: EnquiryFilter; label: string }[] = [
  { value: "needs_action", label: "Needs action" },
  { value: "new", label: "New" },
  { value: "drafted", label: "Drafted" },
  { value: "awaiting_review", label: "Awaiting review" },
  { value: "approved", label: "Approved" },
  { value: "sent", label: "Sent" },
  { value: "all", label: "All" },
];

// Unmatched calls are read in one page. At ELE's volume (a handful of unknown callers a week) 200 is
// far more than will ever be waiting; if it is ever exceeded the page says so rather than silently
// showing a partial list.
const PHONE_PAGE_SIZE = 200;

const ENQUIRY_SEARCH_ACCESSORS: SearchAccessors<EnquiryListRow> = [
  (e) => e.subject,
  (e) => e.counterpartyName,
  (e) => e.counterpartyEmail,
  (e) => e.customerName,
];

const CALL_SEARCH_ACCESSORS: SearchAccessors<CallLogDto> = [(c) => c.callerName, (c) => c.callerPhone, (c) => c.topic];

type Row = { kind: "email"; at: string; e: EnquiryListRow } | { kind: "phone"; at: string; c: CallLogDto };

// A phone enquiry needs action until the office has raised work from it. Nothing infers that from
// the call's outcome field: only the office's own act of raising work writes the link (Session ANV).
export function phoneNeedsAction(c: Pick<CallLogDto, "raisedWork">): boolean {
  return (c.raisedWork ?? []).length === 0;
}

// Where a piece of work raised from a call opens, the same place the Call log tab sends the office.
function raisedWorkHref(work: RaisedWorkRef): string {
  return work.kind === "callout" ? "/office/callouts" : `/office/jobsheets?job=${encodeURIComponent(work.id)}`;
}

const ROW_ACTION = "font-menu text-xs tracking-wide transition hover:text-ele-strong";

export default function EnquiriesPage() {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState<EnquiryListRow[]>([]);
  const [calls, setCalls] = useState<CallLogDto[]>([]);
  const [callsTotal, setCallsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  // The phone half is read separately and best-effort: a failed call feed must not hide the email
  // enquiries, but the office is told the phone ones are missing rather than shown a list that looks
  // complete.
  const [callsError, setCallsError] = useState<string | null>(null);
  const [filter, setFilter] = useState<EnquiryFilter>("needs_action");
  const { search, setSearch } = useListSearch("search");
  // Session ATM, Part C: a separate view within this same page, never a new nav route (the
  // Director's "fold into the existing page if cleaner" option). Defaults to "queue" so every
  // existing behaviour on this page is completely unchanged until the office switches tabs.
  const [view, setView] = useState<PageView>("queue");

  const [notice, setNotice] = useState<{ text: string; link?: { to: string; label: string } } | null>(null);
  const [matching, setMatching] = useState<CallLogDto | null>(null);
  const [accepting, setAccepting] = useState<CallLogDto | null>(null);
  const [raising, setRaising] = useState<CallLogDto | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  async function loadCalls() {
    setCallsError(null);
    try {
      const res = await api.callLogs.list({ unmatched: true, pageSize: PHONE_PAGE_SIZE });
      setCalls(res.items);
      setCallsTotal(res.total);
    } catch (err) {
      setCalls([]);
      setCallsTotal(0);
      setCallsError(friendlyApiError(err, "The phone enquiries could not be loaded, so only email enquiries are shown."));
    }
  }

  async function load() {
    setLoading(true);
    setListError(null);
    try {
      const [res] = await Promise.all([api.enquiries.list(), loadCalls()]);
      setEnquiries(res.enquiries);
    } catch (err) {
      setListError(friendlyApiError(err, "Could not load the enquiries."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function removeCall(call: CallLogDto) {
    setDeletingId(call.id);
    setRowError(null);
    try {
      await api.callLogs.remove(call.id);
      setCalls((list) => list.filter((c) => c.id !== call.id));
      setCallsTotal((n) => Math.max(0, n - 1));
      setNotice({ text: `The call from ${callerLine(call)} was deleted.` });
    } catch (err) {
      setRowError(friendlyApiError(err, "Could not delete that call. Please try again."));
    } finally {
      setDeletingId(null);
    }
  }

  const emailRows: Row[] = filterRows(
    enquiries.filter((e) => {
      if (filter === "all") return true;
      // A conversation ELE has already replied to (from Gmail, by hand) is not waiting on the office, whatever the review
      // queue's own status says (Session BCF: the Mail workspace's derived state, so the two pages agree).
      if (filter === "needs_action") return e.status !== "sent" && e.mailState !== "answered";
      return e.status === filter;
    }),
    search,
    ENQUIRY_SEARCH_ACCESSORS,
  ).map((e) => ({ kind: "email" as const, at: e.lastMessageAt, e }));
  const phoneRows: Row[] =
    filter === "all" || filter === "needs_action"
      ? filterRows(
          calls.filter((c) => filter === "all" || phoneNeedsAction(c)),
          search,
          CALL_SEARCH_ACCESSORS,
        ).map((c) => ({ kind: "phone" as const, at: c.calledAt, c }))
      : [];
  // Newest first across both kinds, matching the raw triage view's own ordering.
  const filtered = [...emailRows, ...phoneRows].sort((a, b) => b.at.localeCompare(a.at));
  const nothingAtAll = enquiries.length === 0 && calls.length === 0;

  return (
    <div>
      <PageHeader
        title="Enquiries"
        subtitle="Email enquiries flagged as needing a reply, each with an AI-drafted reply for you to check before it is sent, and phone calls from callers not yet matched to a customer."
        className="mb-4"
      />

      <div className="mb-4 flex gap-2" role="tablist" aria-label="Enquiries view">
        <button
          type="button"
          role="tab"
          aria-selected={view === "queue"}
          onClick={() => setView("queue")}
          className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
            view === "queue" ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
          }`}
        >
          Queue
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === "bulk"}
          onClick={() => setView("bulk")}
          className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
            view === "bulk" ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
          }`}
        >
          Bulk approve
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === "clear"}
          onClick={() => setView("clear")}
          className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
            view === "clear" ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
          }`}
        >
          Clear old
        </button>
      </div>

      {view === "clear" ? (
        <EnquiryBacklogPanel onChanged={() => void load()} />
      ) : view === "bulk" ? (
        loading ? (
          <Loader label="Loading the enquiries..." />
        ) : listError ? (
          <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
            <p className="text-ele-strong">{listError}</p>
            <button
              type="button"
              onClick={() => void load()}
              className="mt-3 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-blue transition hover:text-ele-strong"
            >
              Retry
            </button>
          </div>
        ) : (
          <BulkApproveView enquiries={enquiries} onRefresh={load} />
        )
      ) : (
        <>
      {notice && (
        <p role="status" className="mb-3 rounded-ele border border-ele-green bg-ele-field-inset px-3 py-2 text-sm text-ele-strong">
          {notice.text}
          {notice.link && (
            <>
              {" "}
              <Link to={notice.link.to} className="text-ele-green underline-offset-2 hover:underline">
                {notice.link.label}
              </Link>
            </>
          )}
        </p>
      )}

      {callsError && !loading && !listError && (
        <div role="alert" className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          <span>{callsError}</span>
          <button type="button" onClick={() => void loadCalls()} className="font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong">
            Retry phone enquiries
          </button>
        </div>
      )}

      {rowError && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {rowError}
        </p>
      )}

      {!loading && !listError && !nothingAtAll && (
        <ListToolbar
          className="mb-3"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search subject, sender, caller or customer..."
          searchLabel="Search enquiries"
          filters={FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              aria-pressed={filter === f.value}
              className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
                filter === f.value ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
              }`}
            >
              {f.label}
            </button>
          ))}
        />
      )}

      {callsTotal > calls.length && !callsError && (
        <p className="mb-3 text-xs text-ele-body">
          Showing the {calls.length} most recent unmatched calls of {callsTotal}. Match or delete older ones to bring the rest into view.
        </p>
      )}

      {loading ? (
        <Loader label="Loading the enquiries..." />
      ) : listError ? (
        <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
          <p className="text-ele-strong">{listError}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-3 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-blue transition hover:text-ele-strong"
          >
            Retry
          </button>
        </div>
      ) : nothingAtAll ? (
        <EmptyState
          message="Nothing needs a reply right now."
          hint="Enquiries appear here automatically when an email needs a response from ELE, and when a call from someone not yet matched to a customer is logged."
        />
      ) : filtered.length === 0 ? (
        <EmptyState message={search.trim() ? `No enquiries match "${search.trim()}".` : "No enquiries match this filter."} />
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "36%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "20%" }} />
              <col className="max-md:hidden" style={{ width: "14%" }} />
              <col style={{ width: "20%" }} />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <th className="px-3 py-2">Enquiry</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Customer</th>
                <th className="max-md:hidden px-3 py-2">Last contact</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) =>
                row.kind === "email" ? (
                  <tr
                    key={`email-${row.e.id}`}
                    className="cursor-pointer border-t border-ele-purple-accent transition hover:bg-ele-inset"
                    onClick={() => navigate(`/office/enquiries/${encodeURIComponent(row.e.id)}`)}
                  >
                    <td className="px-3 py-2 align-top">
                      <div className="font-heading text-ele-strong">{row.e.subject?.trim() || "(no subject)"}</div>
                      <div className="mt-0.5 truncate text-xs text-ele-body opacity-80">{counterpartyLine(row.e)}</div>
                      {row.e.draftTier && (
                        <span
                          className={`mt-1 block font-menu text-[0.6rem] uppercase tracking-wide ${
                            row.e.draftTier === "novel" ? "text-amber-400" : "text-ele-body opacity-70"
                          }`}
                        >
                          {DRAFT_TIER_LABEL[row.e.draftTier]}
                        </span>
                      )}
                      {enquiryDraftSource(row.e) && (
                        <span className="mt-0.5 block truncate font-menu text-[0.6rem] uppercase tracking-wide text-ele-body opacity-70" data-testid={`enquiry-draft-source-${row.e.id}`}>
                          {enquiryDraftSource(row.e)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <StatusPill word="Email" tone="grey" />
                    </td>
                    <td className="px-3 py-2 align-top">
                      {row.e.customerId ? row.e.customerName : <StatusPill word="Unmatched" tone="grey" />}
                    </td>
                    <td className="max-md:hidden px-3 py-2 align-top">{formatDateTime(row.e.lastMessageAt)}</td>
                    <td className="px-3 py-2 align-top">
                      {(() => {
                        // Session BCF: the review queue's own status says nothing of the engine's draft waiting in Gmail or of a
                        // reply sent by hand. The Mail workspace's derived state is shown where it differs, so this page and
                        // that one read the same.
                        if (row.e.status !== "sent" && row.e.mailState === "answered") {
                          return <StatusPill word="Answered in Gmail" tone={statusEntry("enquiry", "sent").tone} />;
                        }
                        if ((row.e.status === "new" || row.e.status === "drafted") && row.e.mailState === "reply") {
                          return <StatusPill word="Reply ready in Gmail" tone={statusEntry("enquiry", "awaiting_review").tone} />;
                        }
                        return <StatusPill word={ENQUIRY_STATUS_LABEL[row.e.status]} tone={statusEntry("enquiry", row.e.status).tone} />;
                      })()}
                    </td>
                  </tr>
                ) : (
                  <tr key={`phone-${row.c.id}`} className="border-t border-ele-purple-accent" data-testid={`phone-enquiry-${row.c.id}`}>
                    <td className="px-3 py-2 align-top">
                      <div className="font-heading text-ele-strong">{row.c.callerName?.trim() || "Unknown caller"}</div>
                      <div className="mt-0.5 truncate text-xs text-ele-body opacity-80">
                        {[row.c.callerPhone, row.c.topic].filter((v) => v && v.trim().length > 0).join(" · ") || "No number or topic recorded"}
                      </div>
                      {(row.c.raisedWork ?? []).length > 0 && (
                        <ul className="mt-1 text-xs">
                          {(row.c.raisedWork ?? []).map((w) => (
                            <li key={`${w.kind}-${w.id}`}>
                              <Link to={raisedWorkHref(w)} className="text-ele-green underline-offset-2 hover:underline">
                                {raisedWorkLabel(w)}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <button
                          type="button"
                          onClick={() => {
                            setNotice(null);
                            setRowError(null);
                            setMatching(row.c);
                          }}
                          className={`${ROW_ACTION} text-ele-blue`}
                        >
                          Match to a customer
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setNotice(null);
                            setRowError(null);
                            setAccepting(row.c);
                          }}
                          className={`${ROW_ACTION} text-ele-green`}
                        >
                          Raise work
                        </button>
                        <ConfirmButton
                          onConfirm={() => void removeCall(row.c)}
                          busy={deletingId === row.c.id}
                          className={`${ROW_ACTION} text-ele-body`}
                          title={`Delete the call from ${callerLine(row.c)}`}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <StatusPill word="Phone" tone="green" />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <StatusPill word="Unmatched" tone="grey" />
                    </td>
                    <td className="max-md:hidden px-3 py-2 align-top">{formatDateTime(row.c.calledAt)}</td>
                    <td className="px-3 py-2 align-top">
                      {phoneNeedsAction(row.c) ? <StatusPill word="No work raised" tone="grey" /> : <StatusPill word="Work raised" tone="green" />}
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}

      {matching && (
        <MatchCallModal
          call={matching}
          onClose={() => setMatching(null)}
          onMatched={(customer) => {
            const matchedId = matching.id;
            setMatching(null);
            // It is no longer an unknown caller, so it leaves this page for the customer's own tab.
            setCalls((list) => list.filter((c) => c.id !== matchedId));
            setCallsTotal((n) => Math.max(0, n - 1));
            setNotice({
              text: `Matched to ${customer.name}. The call is now on their Call log tab.`,
              link: { to: `/office/customers/${encodeURIComponent(customer.id)}`, label: `Open ${customer.name}` },
            });
          }}
        />
      )}

      {/* The same two modals the customer Call log tab uses (Session AQS, W-1), never a copy. */}
      {accepting && (
        <AcceptCallAsWorkModal
          call={accepting}
          onClose={() => setAccepting(null)}
          onCallout={() => {
            // An unknown caller carries no customer, so only the call is carried: the Log call-out
            // form walks the office through the customer, site and lift (or creating them).
            navigate(`/office/callouts?${new URLSearchParams({ new: "log", callLogId: accepting.id }).toString()}`);
          }}
          onPlannedWork={() => {
            setRaising(accepting);
            setAccepting(null);
          }}
          onQuote={() => {
            // A call holds no site or lift, and this caller no customer, so the prefill is only the
            // name they gave, for the office to correct.
            navigate("/office/quotes/new", { state: { prefill: { customerName: accepting.callerName ?? "" } } });
            setAccepting(null);
          }}
        />
      )}

      {raising && (
        <RaiseWorkModal
          preset={{ type: "repair", callLogId: raising.id, callerLabel: callerLine(raising) }}
          onClose={() => setRaising(null)}
          onRaised={(result) => {
            setRaising(null);
            setNotice({ text: raisedWorkMessage(result) });
            // Re-read the calls, so the row shows the work it raised and leaves "Needs action".
            void loadCalls();
          }}
        />
      )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Session ATM, Part C: the bulk-approve screen. Two structurally separate sections -- "Ready to
// send" (checkboxes, bulk approve/reject) and "Needs a decision" (read-only, never a checkbox,
// never in the same array as the bulk list) -- so a future edit to the bulk list cannot
// accidentally sweep a judgement-call draft into a bulk send.
// ---------------------------------------------------------------------------------------------

export function BulkApproveView({ enquiries, onRefresh }: { enquiries: EnquiryListRow[]; onRefresh: () => Promise<void> }) {
  const ready = readyRowsOf(enquiries);
  const needsDecision = needsDecisionRowsOf(enquiries);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [details, setDetails] = useState<Map<string, EnquiryDetail>>(new Map());
  const [detailErrors, setDetailErrors] = useState<Map<string, string>>(new Map());
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [confirmSend, setConfirmSend] = useState(false);
  const [confirmReject, setConfirmReject] = useState(false);
  const [busyBulk, setBusyBulk] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // A refresh from the parent can move a row out of "ready" (sent, rejected, reassigned by
  // someone else): drop it from the selection rather than leave a stale id armed for a send.
  useEffect(() => {
    setSelected((s) => {
      if (s.size === 0) return s;
      const readyIds = new Set(ready.map((r) => r.id));
      const next = new Set([...s].filter((id) => readyIds.has(id)));
      return next.size === s.size ? s : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enquiries]);

  async function loadDetail(id: string) {
    setLoadingIds((s) => new Set(s).add(id));
    setDetailErrors((m) => {
      const n = new Map(m);
      n.delete(id);
      return n;
    });
    try {
      const res = await api.enquiries.get(id);
      setDetails((m) => new Map(m).set(id, res.enquiry));
    } catch (err) {
      setDetailErrors((m) => new Map(m).set(id, friendlyApiError(err, "Could not load the full reply.")));
    } finally {
      setLoadingIds((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
    }
  }

  function ensureLoaded(id: string) {
    if (!details.has(id) && !loadingIds.has(id)) void loadDetail(id);
  }

  function toggleExpand(id: string) {
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
    ensureLoaded(id);
  }

  // Selecting a row for the bulk send auto-expands it: never let a bulk send go out on an
  // unread draft (docs/specs/ELE-V2-SESSION-ATM-SPEC.md, Part C.2).
  function toggleSelect(id: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
    setExpanded((s) => new Set(s).add(id));
    ensureLoaded(id);
  }

  function toggleSelectAll() {
    if (selected.size === ready.length && ready.length > 0) {
      setSelected(new Set());
      return;
    }
    const ids = ready.map((r) => r.id);
    setSelected(new Set(ids));
    setExpanded((s) => new Set([...s, ...ids]));
    ids.forEach(ensureLoaded);
  }

  const selectedList = ready.filter((r) => selected.has(r.id));
  const allSelectedRead = selectedList.length > 0 && selectedList.every((r) => details.has(r.id));

  function startEdit(id: string) {
    const d = details.get(id);
    if (!d) return;
    setEditingId(id);
    setEditSubject(d.draftContent?.subject ?? "");
    setEditBody(d.draftContent?.bodyHtml ?? "");
    setEditError(null);
  }

  async function saveEdit() {
    if (!editingId) return;
    setSavingEdit(true);
    setEditError(null);
    try {
      const res = await api.enquiries.updateDraft(editingId, { subject: editSubject, bodyHtml: editBody });
      setDetails((m) => new Map(m).set(editingId, res.enquiry));
      setEditingId(null);
    } catch (err) {
      setEditError(friendlyApiError(err, "Could not save your changes. Please try again."));
    } finally {
      setSavingEdit(false);
    }
  }

  // Per row, one call each, sequentially -- exactly as the spec requires (no new bulk-send
  // route; each is individually audited exactly as approve-and-send already is today).
  // Session ATW: each send carries the recipient the confirm named (expectedTo), read from that enquiry's own
  // conversation, so a reply never goes to someone the office was not shown.
  async function performSend(recipients: Map<string, string>) {
    setBusyBulk(true);
    setBulkError(null);
    const ids = [...selected];
    let successCount = 0;
    const failures: string[] = [];
    for (const id of ids) {
      try {
        await api.enquiries.approveAndSend(id, recipients.get(id));
        successCount++;
      } catch (err) {
        const row = ready.find((r) => r.id === id);
        failures.push(`${row?.subject?.trim() || row?.counterpartyEmail || id}: ${friendlyApiError(err, "could not be sent")}`);
      }
    }
    setBusyBulk(false);
    setConfirmSend(false);
    setSelected(new Set());
    setNotice(
      failures.length === 0
        ? `Sent ${successCount} repl${successCount === 1 ? "y" : "ies"}.`
        : `Sent ${successCount} repl${successCount === 1 ? "y" : "ies"}. ${failures.length} could not be sent: ${failures.join("; ")}`,
    );
    await onRefresh();
  }

  async function performReject() {
    setBusyBulk(true);
    setBulkError(null);
    const ids = [...selected];
    let successCount = 0;
    const failures: string[] = [];
    for (const id of ids) {
      try {
        await api.enquiries.rejectDraft(id);
        successCount++;
      } catch (err) {
        const row = ready.find((r) => r.id === id);
        failures.push(`${row?.subject?.trim() || row?.counterpartyEmail || id}: ${friendlyApiError(err, "could not be rejected")}`);
      }
    }
    setBusyBulk(false);
    setConfirmReject(false);
    setSelected(new Set());
    setNotice(
      failures.length === 0
        ? `Rejected ${successCount} draft${successCount === 1 ? "" : "s"}. Each is back in "New" for a fresh draft.`
        : `Rejected ${successCount} draft${successCount === 1 ? "" : "s"}. ${failures.length} could not be rejected: ${failures.join("; ")}`,
    );
    await onRefresh();
  }

  return (
    <div>
      {notice && (
        <p role="status" className="mb-3 rounded-ele border border-ele-green bg-ele-field-inset px-3 py-2 text-sm text-ele-strong">
          {notice}
        </p>
      )}

      <section className="mb-6 rounded-ele border border-ele-purple-accent bg-ele-surface p-4" aria-label="Ready to send">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-lg text-ele-strong">Ready to send ({ready.length})</h2>
          {ready.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={toggleSelectAll} className="font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong">
                {selected.size === ready.length ? "Deselect all" : "Select all"}
              </button>
              <Button size="sm" variant="primary" disabled={selected.size === 0 || !allSelectedRead || busyBulk} onClick={() => setConfirmSend(true)}>
                Approve & send selected ({selected.size})
              </Button>
              <Button size="sm" variant="destructive" disabled={selected.size === 0 || busyBulk} onClick={() => setConfirmReject(true)}>
                Reject selected ({selected.size})
              </Button>
            </div>
          )}
        </div>
        {selected.size > 0 && !allSelectedRead && (
          <p className="mb-3 text-xs text-ele-body">Reading the full reply for every selected row before you can send. Loading...</p>
        )}
        {bulkError && (
          <p role="alert" className="mb-3 text-sm text-ele-danger">
            {bulkError}
          </p>
        )}
        {ready.length === 0 ? (
          <p className="text-sm text-ele-body">Nothing is ready to send in bulk right now.</p>
        ) : (
          <ul className="divide-y divide-ele-hairline">
            {ready.map((e) => {
              const isExpanded = expanded.has(e.id);
              const detail = details.get(e.id);
              const detailError = detailErrors.get(e.id);
              const isLoadingDetail = loadingIds.has(e.id);
              const isEditing = editingId === e.id;
              return (
                <li key={e.id} className="py-3" data-testid={`ready-row-${e.id}`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      aria-label={`Select ${e.subject?.trim() || "this enquiry"}`}
                      checked={selected.has(e.id)}
                      onChange={() => toggleSelect(e.id)}
                      className="mt-1 accent-ele-green"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="font-heading text-ele-strong">{e.subject?.trim() || "(no subject)"}</span>
                        <Link to={`/office/enquiries/${encodeURIComponent(e.id)}`} className="text-xs text-ele-blue underline-offset-2 hover:underline">
                          Open full record
                        </Link>
                      </div>
                      <div className="text-xs text-ele-body opacity-80">{counterpartyLine(e)}</div>
                      <p className="mt-1 text-sm text-ele-body">
                        {detail?.draftContent ? draftBodySummary(detail.draftContent.bodyHtml) : e.contentReason || "..."}
                      </p>
                      <button
                        type="button"
                        onClick={() => toggleExpand(e.id)}
                        className="mt-1 font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong"
                      >
                        {isExpanded ? "Hide full reply" : "Read full reply"}
                      </button>

                      {isExpanded && (
                        <div className="mt-2 rounded-ele border border-ele-hairline bg-ele-inset p-3">
                          {isLoadingDetail ? (
                            <Loader shape="inline" />
                          ) : detailError ? (
                            <div>
                              <p role="alert" className="text-sm text-ele-danger">
                                {detailError}
                              </p>
                              <button type="button" onClick={() => void loadDetail(e.id)} className="mt-1 font-menu text-xs text-ele-blue underline">
                                Retry
                              </button>
                            </div>
                          ) : detail?.draftContent ? (
                            isEditing ? (
                              <div>
                                <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body" htmlFor={`edit-subject-${e.id}`}>
                                  Subject
                                </label>
                                <input
                                  id={`edit-subject-${e.id}`}
                                  value={editSubject}
                                  onChange={(ev) => setEditSubject(ev.target.value)}
                                  className="mb-2 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-sm text-ele-strong"
                                />
                                <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body" htmlFor={`edit-body-${e.id}`}>
                                  Message (HTML)
                                </label>
                                <textarea
                                  id={`edit-body-${e.id}`}
                                  value={editBody}
                                  onChange={(ev) => setEditBody(ev.target.value)}
                                  rows={6}
                                  className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-sm text-ele-strong"
                                />
                                {editError && (
                                  <p role="alert" className="mt-1 text-xs text-ele-danger">
                                    {editError}
                                  </p>
                                )}
                                <div className="mt-2 flex gap-2">
                                  <Button size="sm" variant="secondary" onClick={() => void saveEdit()} disabled={savingEdit}>
                                    {savingEdit ? "Saving..." : "Save changes"}
                                  </Button>
                                  <button
                                    type="button"
                                    onClick={() => setEditingId(null)}
                                    disabled={savingEdit}
                                    className="font-menu text-xs text-ele-body hover:text-ele-strong"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <p className="mb-1 text-xs text-ele-body opacity-70">Subject: {detail.draftContent.subject}</p>
                                <iframe
                                  title={`Reply preview for ${e.id}`}
                                  srcDoc={detail.draftContent.bodyHtml}
                                  sandbox=""
                                  className="h-48 w-full rounded-ele border border-ele-hairline bg-white"
                                />
                                <button
                                  type="button"
                                  onClick={() => startEdit(e.id)}
                                  className="mt-2 font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong"
                                >
                                  Edit this reply
                                </button>
                              </div>
                            )
                          ) : (
                            <p className="text-sm text-ele-body">No draft content to show.</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Structurally separate markup, never a checkbox, never in the bulk array above. */}
      <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4" aria-label="Needs a decision">
        <h2 className="mb-3 font-heading text-lg text-ele-strong">Needs a decision ({needsDecision.length})</h2>
        {needsDecision.length === 0 ? (
          <p className="text-sm text-ele-body">Nothing needs a hand-written reply right now.</p>
        ) : (
          <ul className="space-y-3">
            {needsDecision.map((e) => (
              <NeedsDecisionCard key={e.id} enquiry={e} />
            ))}
          </ul>
        )}
      </section>

      {confirmSend && (
        <EnquirySendConfirmModal
          targets={selectedList.map((r) => ({ id: r.id, label: r.subject?.trim() || r.counterpartyEmail || r.counterpartyName || "Enquiry" }))}
          busy={busyBulk}
          error={bulkError}
          onConfirm={(recipients) => void performSend(recipients)}
          onCancel={() => !busyBulk && setConfirmSend(false)}
        />
      )}

      <ConfirmDeleteModal
        open={confirmReject}
        variant="archive"
        title={`Reject ${selected.size} drafted repl${selected.size === 1 ? "y" : "ies"}?`}
        message={`This clears the draft${selected.size === 1 ? "" : "s"} and puts ${
          selected.size === 1 ? "the enquiry" : "each enquiry"
        } back to "New" so a fresh draft can be generated. Nothing is emailed.`}
        confirmLabel="Reject drafts"
        busy={busyBulk}
        error={bulkError}
        onConfirm={() => void performReject()}
        onCancel={() => !busyBulk && setConfirmReject(false)}
      />
    </div>
  );
}

// Read-only card for the "Needs a decision" section. The judgement-category summary (Part B)
// lives in the audit trail's "enquiry_draft_generated" entry (mirroring what
// EnquiryDetailPage.tsx already points the office at); AuditLog.after is opaque Json, so the
// read is defensive and falls back to the enquiry's own contentReason -- the closest available
// text -- if no summary field is present. See this session's handoff for why: the list DTO
// carries no draft-tier summary field today.
function NeedsDecisionCard({ enquiry }: { enquiry: EnquiryListRow }) {
  const [shown, setShown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  async function loadNote() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.auditLog.list("enquiry", enquiry.id);
      const entry = [...res.entries].reverse().find((en) => en.action === "enquiry_draft_generated");
      const after = entry?.after;
      const summary =
        after && typeof after === "object" && "summary" in (after as Record<string, unknown>) && typeof (after as Record<string, unknown>).summary === "string"
          ? ((after as Record<string, unknown>).summary as string)
          : null;
      setNote(summary ?? enquiry.contentReason ?? "No note was recorded for this enquiry.");
    } catch (err) {
      setError(friendlyApiError(err, "Could not load the note for this enquiry."));
    } finally {
      setLoading(false);
    }
  }

  return (
    <li className="rounded-ele border border-ele-hairline bg-ele-inset p-3" data-testid={`needs-decision-${enquiry.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-heading text-ele-strong">{enquiry.subject?.trim() || "(no subject)"}</span>
        <Link to={`/office/enquiries/${encodeURIComponent(enquiry.id)}`} className="text-xs text-ele-blue underline-offset-2 hover:underline">
          Open
        </Link>
      </div>
      <div className="text-xs text-ele-body opacity-80">{counterpartyLine(enquiry)}</div>
      {!shown ? (
        <button
          type="button"
          onClick={() => {
            setShown(true);
            void loadNote();
          }}
          className="mt-2 font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong"
        >
          Show note
        </button>
      ) : loading ? (
        <Loader shape="inline" className="mt-2" />
      ) : error ? (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      ) : (
        <p className="mt-2 text-sm text-ele-body">{note}</p>
      )}
    </li>
  );
}
